/**
 * Resolves which one-time fees apply given the current cart state and existing account products.
 *
 * Each fee's `appliesTo` is an array of rule objects (from the product_applies_to table):
 *   {
 *     appliesProduct:      string        — the product code this rule matches against
 *     excludedIfExisting:  string[]|null — skip rule if ANY of these codes are in existingCodes
 *     excludedIfAdding:    string[]|null — skip rule if ANY of these codes are also in cartCodes
 *     weight:              number|null   — null = independent fee (always applied if matched);
 *                                         number = competes with other weighted fees (highest wins)
 *   }
 *
 * Resolution algorithm:
 *   1. For each fee, find the best-matching rule across all cartCodes
 *      (most specific = most non-null conditions satisfied; ties broken by weight)
 *   2. Independent fees (weight=null) are always included if any rule matched
 *   3. Weighted fees compete globally — only the single highest-specificity+weight winner is included
 *
 * @param {string[]} existingCodes   - Product codes the account already has
 * @param {string[]} cartCodes       - All product codes in the cart (new + existing)
 * @param {Array}    feeDefinitions  - oneTimeFees catalog array (each has .code and .appliesTo)
 * @returns {string[]} - fee .code values that should be present in the cart
 */
export function resolveApplicableFees(
  existingCodes,
  cartCodes,
  feeDefinitions,
) {
  // Only newly added codes trigger fee rules; existing products are already paid for
  const newCodes = cartCodes.filter(code => !existingCodes.includes(code))
  const weightedCandidates = []
  const independentResults = []

  for (const fee of feeDefinitions) {
    if (!fee.appliesTo?.length) continue
    let bestRule = null

    for (const cartCode of newCodes) {
      const matchingRules = fee.appliesTo.filter(rule => {
        if (rule.appliesProduct !== cartCode) return false
        if (rule.excludedIfExisting?.some(c => existingCodes.includes(c)))
          return false
        if (
          rule.excludedIfAdding?.some(
            c => newCodes.includes(c) && c !== cartCode,
          )
        )
          return false
        return true
      })

      for (const rule of matchingRules) {
        const spec =
          (rule.excludedIfExisting != null ? 1 : 0) +
          (rule.excludedIfAdding != null ? 1 : 0)
        const bestSpec =
          bestRule != null
            ? (bestRule.excludedIfExisting != null ? 1 : 0) +
              (bestRule.excludedIfAdding != null ? 1 : 0)
            : -1

        if (
          spec > bestSpec ||
          (spec === bestSpec && (rule.weight ?? 0) > (bestRule?.weight ?? 0))
        ) {
          bestRule = rule
        }
      }
    }

    if (bestRule == null) continue

    if (bestRule.weight == null) {
      independentResults.push(fee.code)
    } else {
      weightedCandidates.push({feeCode: fee.code, rule: bestRule})
    }
  }

  // Among weighted fees, pick the single winner (highest specificity, then weight)
  let winnerCode = null
  if (weightedCandidates.length > 0) {
    const winner = weightedCandidates.sort((a, b) => {
      const specA =
        (a.rule.excludedIfExisting != null ? 1 : 0) +
        (a.rule.excludedIfAdding != null ? 1 : 0)
      const specB =
        (b.rule.excludedIfExisting != null ? 1 : 0) +
        (b.rule.excludedIfAdding != null ? 1 : 0)
      if (specB !== specA) return specB - specA
      return (b.rule.weight ?? 0) - (a.rule.weight ?? 0)
    })[0]
    winnerCode = winner.feeCode
  }

  const result = [...independentResults, ...(winnerCode ? [winnerCode] : [])]
  return result
}
